import React, { useState } from 'react';
import { IntroScreen } from './components/IntroScreen';
import { ForcedChoiceQuestion } from './components/ForcedChoiceQuestion';
import { ScaleQuestion } from './components/ScaleQuestion';
import { ResultsScreen } from './components/ResultsScreen';
import { ProgressBar } from './components/ProgressBar';
import { FORCED_CHOICE_QUESTIONS, SCALE_QUESTIONS } from './data/questions';
import { calculateResults } from './utils/scoring';
import { AssessmentState, AssessmentResult } from './types/assessment';
function generateRandomResult(): AssessmentResult {
  const profiles = [
  // Hunter: high drive, high di, low sc, high forced
  () => {
    const drive = 20 + Math.floor(Math.random() * 5);
    const di = 18 + Math.floor(Math.random() * 7);
    const sc = 8 + Math.floor(Math.random() * 7);
    const forced = 7 + Math.floor(Math.random() * 4);
    return {
      drive,
      di,
      sc,
      forced
    };
  },
  // Closer: high drive, moderate-high di, moderate sc, good forced
  () => {
    const drive = 18 + Math.floor(Math.random() * 6);
    const di = 15 + Math.floor(Math.random() * 8);
    const sc = 12 + Math.floor(Math.random() * 7);
    const forced = 6 + Math.floor(Math.random() * 4);
    return {
      drive,
      di,
      sc,
      forced
    };
  },
  // Steady Performer: moderate drive, any di, high sc
  () => {
    const drive = 12 + Math.floor(Math.random() * 7);
    const di = 10 + Math.floor(Math.random() * 10);
    const sc = 18 + Math.floor(Math.random() * 7);
    const forced = 5 + Math.floor(Math.random() * 5);
    return {
      drive,
      di,
      sc,
      forced
    };
  }];
  const generator = profiles[Math.floor(Math.random() * profiles.length)];
  const raw = generator();
  // Clamp scores to valid range (5-25 for scale, 0-10 for forced)
  const drive_score = Math.min(25, Math.max(5, raw.drive));
  const di_score = Math.min(25, Math.max(5, raw.di));
  const sc_score = Math.min(25, Math.max(5, raw.sc));
  const forced_score = Math.min(10, Math.max(0, raw.forced));
  const toPercent = (score: number) => Math.round((score - 5) / 20 * 100);
  let final_profile = 'Review Manually';
  if (drive_score < 12 || forced_score <= 4) {
    final_profile = 'At-Risk';
  } else if (drive_score >= 20 && di_score >= 18 && sc_score <= 15 && forced_score >= 7) {
    final_profile = 'Hunter';
  } else if (drive_score >= 18 && di_score >= 15 && sc_score >= 12 && sc_score <= 18 && forced_score >= 6) {
    final_profile = 'Closer';
  } else if (drive_score >= 12 && drive_score <= 18 && sc_score >= 18) {
    final_profile = 'Steady Performer';
  }
  return {
    forced_score,
    drive_score,
    di_score,
    sc_score,
    drive_pct: toPercent(drive_score),
    di_pct: toPercent(di_score),
    sc_pct: toPercent(sc_score),
    possible_gaming: false,
    gaming_reasons: [],
    confidence_level: forced_score >= 7 ? 'High' : forced_score >= 5 ? 'Moderate' : 'Low',
    final_profile
  };
}
export function App() {
  const [state, setState] = useState<AssessmentState>({
    status: 'intro',
    currentQuestionIndex: 0,
    forcedAnswers: [],
    scaleAnswers: [],
    result: null
  });
  const handleStart = () => {
    setState(prev => ({
      ...prev,
      status: 'forced',
      currentQuestionIndex: 0
    }));
  };
  const handlePreview = () => {
    const result = generateRandomResult();
    setState(prev => ({
      ...prev,
      status: 'preview',
      result
    }));
  };
  const handleBack = () => {
    if (state.status === 'forced') {
      if (state.currentQuestionIndex > 0) {
        // Go back one forced question, keep the answer so it shows as selected
        setState(prev => ({
          ...prev,
          currentQuestionIndex: prev.currentQuestionIndex - 1
        }));
      } else {
        // First forced question → back to intro
        setState(prev => ({
          ...prev,
          status: 'intro',
          currentQuestionIndex: 0,
          forcedAnswers: []
        }));
      }
    } else if (state.status === 'scale') {
      if (state.currentQuestionIndex > 0) {
        // Go back one scale question, keep the answer
        setState(prev => ({
          ...prev,
          currentQuestionIndex: prev.currentQuestionIndex - 1
        }));
      } else {
        // First scale question → back to last forced question
        setState(prev => ({
          ...prev,
          status: 'forced',
          currentQuestionIndex: FORCED_CHOICE_QUESTIONS.length - 1
        }));
      }
    }
  };
  const handleForcedAnswer = (value: 0 | 1) => {
    // Replace answer at current index or append
    const newAnswers = [...state.forcedAnswers];
    newAnswers[state.currentQuestionIndex] = value;
    // Trim any answers after current index (in case we went back and changed)
    const trimmed = newAnswers.slice(0, state.currentQuestionIndex + 1);
    if (state.currentQuestionIndex < FORCED_CHOICE_QUESTIONS.length - 1) {
      setState(prev => ({
        ...prev,
        forcedAnswers: trimmed,
        currentQuestionIndex: prev.currentQuestionIndex + 1
      }));
    } else {
      setState(prev => ({
        ...prev,
        forcedAnswers: trimmed,
        status: 'scale',
        currentQuestionIndex: 0
      }));
    }
  };
  const handleScaleAnswer = (value: number) => {
    // Replace answer at current index or append
    const newAnswers = [...state.scaleAnswers];
    newAnswers[state.currentQuestionIndex] = value;
    // Trim any answers after current index
    const trimmed = newAnswers.slice(0, state.currentQuestionIndex + 1);
    if (state.currentQuestionIndex < SCALE_QUESTIONS.length - 1) {
      setState(prev => ({
        ...prev,
        scaleAnswers: trimmed,
        currentQuestionIndex: prev.currentQuestionIndex + 1
      }));
    } else {
      const result = calculateResults(state.forcedAnswers, trimmed);
      setState(prev => ({
        ...prev,
        scaleAnswers: trimmed,
        status: 'results',
        result
      }));
    }
  };
  const totalQuestions = FORCED_CHOICE_QUESTIONS.length + SCALE_QUESTIONS.length;
  let currentProgress = 0;
  if (state.status === 'forced') {
    currentProgress = state.currentQuestionIndex;
  } else if (state.status === 'scale') {
    currentProgress = FORCED_CHOICE_QUESTIONS.length + state.currentQuestionIndex;
  } else if (state.status === 'results') {
    currentProgress = totalQuestions;
  }
  return <div className="min-h-screen bg-[#0B0F14] text-[#F5F5F4] font-body selection:bg-[#E10600] selection:text-white" data-id="element-0">
      <main className="container mx-auto max-w-4xl px-4 py-8 md:py-16 min-h-screen flex flex-col" data-id="element-1">
        {/* Header */}
        <div className="mb-12 flex items-center justify-between" data-id="element-2">
          <button onClick={() => setState({
          status: 'intro',
          currentQuestionIndex: 0,
          forcedAnswers: [],
          scaleAnswers: [],
          result: null
        })} className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer group" data-id="element-3">
            <div className="relative" data-id="element-4">
              <img src="https://cdn.magicpatterns.com/uploads/7MGRrFLUXwaBkWiykPzXFd/Screenshot_2026-02-16_at_9.30.02_PM.png" alt="Sales DNA Logo" className="w-10 h-12 object-contain flex-shrink-0" data-id="element-5" />
              <div className="absolute inset-0 bg-[#E10600] blur-xl opacity-20 group-hover:opacity-40 transition-opacity rounded-full" data-id="element-6"></div>
            </div>
            <div className="flex flex-col leading-none" data-id="element-7">
              <span className="text-3xl font-display font-bold tracking-tight text-white" data-id="element-8">
                SALES<span className="text-[#E10600]" data-id="element-9">DNA</span>
              </span>
            </div>
          </button>
        </div>

        {(state.status === 'forced' || state.status === 'scale') && <ProgressBar current={currentProgress + 1} total={totalQuestions} label={`ASSESSMENT PROGRESS`} data-id="element-10" />}

        <div className="flex-grow flex flex-col justify-center" data-id="element-11">
          {state.status === 'intro' && <IntroScreen onStart={handleStart} onPreview={handlePreview} data-id="element-12" />}

          {state.status === 'forced' && <ForcedChoiceQuestion key={`forced-${state.currentQuestionIndex}`} question={FORCED_CHOICE_QUESTIONS[state.currentQuestionIndex]} onAnswer={handleForcedAnswer} onBack={handleBack} canGoBack={true} previousAnswer={state.forcedAnswers[state.currentQuestionIndex]} data-id="element-13" />}

          {state.status === 'scale' && <ScaleQuestion key={`scale-${state.currentQuestionIndex}`} question={SCALE_QUESTIONS[state.currentQuestionIndex]} onAnswer={handleScaleAnswer} onBack={handleBack} canGoBack={true} previousAnswer={state.scaleAnswers[state.currentQuestionIndex]} data-id="element-14" />}

          {state.status === 'results' && state.result && <ResultsScreen result={state.result} data-id="element-15" />}

          {state.status === 'preview' && state.result && <ResultsScreen result={state.result} isPreview data-id="element-16" />}
        </div>

        <footer className="mt-16 text-center text-[#6B7280] text-xs uppercase tracking-widest" data-id="element-17">
          &copy; {new Date().getFullYear()} Sales DNA — Discover Your Sales
          Superpowers
        </footer>
      </main>
    </div>;
}